import { PostReach } from '@shared/posting/outboxStatus';
import type {
  BinInfoDto,
  MisReferenceDto,
  PicklistDto,
  PicklistItemDto,
  PicklistItemGroupDto,
  PicklistPlannedLineDto,
  PostingBatchStatusDto,
} from '../types';
import { DocumentStatus, PostingStatusEnum } from '../types';

/**
 * PW-12 — the picklist landing's quantity maths and status vocabulary, as pure functions.
 *
 * Everything here is a function of a `PicklistDto` and nothing else: no store reads, no hooks, no fetches.
 * That is deliberate — the same numbers have to appear on the landing, the detail header and the post
 * dialog, and the way three surfaces end up disagreeing is each one doing its own arithmetic inline.
 *
 * ## Two arithmetic traps this module exists to close
 *
 * **1. `progressPercentage` is line-count based and must not be used for progress.** The server computes it
 * as `round(count(items where IsPicked) / count(items) * 100, 1)`, so a line picked 190 of 240 contributes
 * **zero**. It was the landing's bar width, its caption, its sort key and its search subtitle. Every figure
 * here is quantity-weighted instead. (The DTO property stays — mobile computes its own copy of it.)
 *
 * **2. `voidedQuantity` must NOT be subtracted.** `quantityPicked` is already a signed pick-ledger sum that
 * includes reversal rows, and legacy voids were backfilled as reversal rows — so `picked − voided`
 * double-subtracts on exactly the historical lines it looks like it is fixing. `PicklistItem.IsVoided` /
 * `VoidedQuantity` have no writer left. (D14.)
 */

/** One picklist's quantities, summed across its lines. */
export interface PicklistAggregate {
  /** Σ quantityToPick */
  toPick: number;
  /** Σ quantityPicked — ledger-net */
  picked: number;
  /** Σ quantityPosted */
  posted: number;
  /**
   * Σ postingQuantity — handed to an open posting batch and on its way to NetSuite (PW-01).
   *
   * The FIELD keeps the internal name; the UI calls it **posting**, never "committed". The later design
   * handoff renamed it because "committed" read as accounting jargon, and as *promised* rather than
   * *on its way* — which is the one thing a picker needs to understand about these units.
   */
  inflight: number;
  /**
   * ITEMS (PT-1 groups) fully picked: every planned line picked to its plan and picked above zero. A two-line
   * item counts once, and only when both lines are done — the row the Items table draws is the item.
   */
  fullyPicked: number;
  /** Item rows — `itemGroups.length`, the same count the server's `totalItems` gives (PT-7 G2). */
  itemCount: number;
  /**
   * The single unit of measure when every line shares one, else null.
   *
   * D30: **35.5% of picklists mix units.** Adding `50 kg + 3 pcs` into one "53 units" total is not a
   * quantity anyone can act on, and it would appear right before an irreversible NetSuite write. So a
   * single combined total is printed only when this is non-null — a mixed picklist shows `byUnit` instead,
   * which is real numbers rather than either a fake total or no total at all.
   */
  unit: string | null;
  /**
   * Per-unit picked/toPick totals, in first-appearance order. A line with no unit of measure is grouped
   * under `"units"` — the same fallback `PostedToNetSuiteCard.totalsByUnit` uses.
   */
  byUnit: { unit: string; picked: number; toPick: number }[];
  /**
   * Headline percentage — **quantity-weighted**: `Σmin(picked, toPick) / ΣtoPick`.
   *
   * ⚠️ This was a mean of per-line ratios until 2026-09-11, which silently made it LINE-weighted — the
   * very defect the rebuild was written to fix (see the "weights progress by quantity" test, which used a
   * one-line picklist and so could never catch it). A four-line picklist with two short lines finished and
   * two long ones untouched read **50%** while its own bar — `segments()`, which has always divided the raw
   * sums — showed a sliver, and the pill tooltip claimed "weighted by quantity, not item count". Three
   * surfaces, two bases, one false label. The owner called it misleading on a live screen and was right.
   *
   * Each line is capped at its target BEFORE summing, so an over-pick on one line still cannot mask a
   * shortfall on another — the one guarantee the mean-of-ratios form was protecting.
   */
  percent: number;
  /** Units of this picklist that would exceed what the MRS still allows, per PW-07's server rule. */
  overRelease: number;
  /**
   * ISS-R-117 — what Post could actually put on a batch right now: per MRS line, what is ready, capped at what the
   * MRS still allows (`mrsLineFigures`).
   *
   * ⚠️ **NOT `picked − posted`.** Units already in an open batch come off (PW-01: the claim refuses them), and a
   * line over its MRS headroom counts only up to the headroom (PW-07: the claim refuses the excess, not the line).
   * Until 2026-10-05 a line over its headroom counted as nothing, so the button read "Blocked" while the dialog
   * and the server would accept a partial post (owner ruling, F-09).
   */
  postable: number;
}

const sum = (items: PicklistItemDto[], pick: (i: PicklistItemDto) => number) =>
  items.reduce((acc, i) => acc + (pick(i) || 0), 0);

export function aggregate(picklist: PicklistDto): PicklistAggregate {
  const items = picklist.items ?? [];
  const toPick = sum(items, (i) => i.quantityToPick);
  const picked = sum(items, (i) => i.quantityPicked);
  const posted = sum(items, (i) => i.quantityPosted);
  const inflight = sum(items, (i) => i.postingQuantity);

  // A line with NO unit of measure is its own group, not an invisible one. This used to `.filter(Boolean)`
  // the blanks away, so a picklist of 4 unitless items plus 2 kg saw a single unit and printed "3 / 6 kg
  // picked" — relabelling the unknown four as kilograms. Unknown is a unit you cannot add to another.
  const units = new Set(items.map((i) => i.unitOfMeasure || 'units'));

  // Quantity-weighted, with each line capped at its own target first: a line picked to 200% contributes
  // its target and no more, so it cannot carry an untouched line's shortfall.
  const effectivePicked = items.reduce(
    (acc, i) => acc + Math.min(i.quantityPicked || 0, i.quantityToPick || 0),
    0,
  );

  const byUnitMap = new Map<string, { unit: string; picked: number; toPick: number }>();
  for (const i of items) {
    const key = i.unitOfMeasure || 'units';
    const row = byUnitMap.get(key) ?? { unit: key, picked: 0, toPick: 0 };
    row.picked += i.quantityPicked || 0;
    row.toPick += i.quantityToPick || 0;
    byUnitMap.set(key, row);
  }

  // Counts are ITEMS (PT-7 G2): `items[]` is one row per MRS line, the record, so counting it would call an
  // item planned for two MRS lines "2 items". The sums above stay per line — each line's over-release and
  // postable figure is the server's own per-line rule.
  const groups = picklist.itemGroups ?? [];
  const mrsLines: OverReleaseLine[] = groups.length > 0 ? groups.flatMap((g) => g.plannedLines ?? []) : items;

  return {
    toPick,
    picked,
    posted,
    inflight,
    fullyPicked: groups.filter(groupFullyPicked).length,
    itemCount: groups.length,
    unit: units.size === 1 ? [...units][0] : null,
    byUnit: [...byUnitMap.values()],
    percent: toPick > 0 ? Math.round((effectivePicked / toPick) * 100) : 0,
    // ISS-R-117 — per MRS line. The planned lines carry what open posts on EVERY picklist hold of the line (by-id
    // read); a read without groups falls back to `items[]`, whose in-flight figure is then taken as none.
    overRelease: mrsLines.reduce((acc, l) => acc + mrsLineFigures(l).over, 0),
    postable: mrsLines.reduce((acc, l) => acc + mrsLineFigures(l).postable, 0),
  };
}

/** Every planned line picked to its plan, and something picked. A group with no lines is not "fully picked". */
function groupFullyPicked(group: PicklistItemGroupDto): boolean {
  const lines = group.plannedLines ?? [];
  return lines.length > 0
    && lines.every((l) => (l.quantityPicked ?? 0) >= (l.quantityPlanned ?? 0) && (l.quantityPicked ?? 0) > 0);
}

/**
 * PW-07's rule, read from cached numbers rather than re-derived. Mirrors `MisSelectionRules
 * .EnsureWithinMrsRelease`: a line with no recorded MRS demand says nothing either way, and must never be
 * read as over-issued just because it was never synced.
 *
 * ⚠️ `mrsReleasedQuantity` is everything ever issued against the MRS line, from every picklist and every
 * other system, and it is CACHED — it moves only on an MRS sync. Never substitute `quantityPosted`, which
 * is this picklist's issuance alone: the swap under-reports released and over-reports headroom.
 */
export function itemOverRelease(line: OverReleaseLine): number {
  return mrsLineFigures(line).over;
}

/**
 * ISS-R-117 — one MRS line's posting figures, the arithmetic of the Post dialog (`mrsHeadroom`) and of the claim:
 *
 * - `ready`: picked, less posted, less what this picklist's open batches hold;
 * - `headroom`: requested, less released, less what open posts on every picklist hold of the line;
 * - `over`: how far `ready` overshoots `headroom`;
 * - `postable`: `ready` capped at `headroom`.
 *
 * A line with no recorded MRS demand is never over and all of it is postable: "not synced" is not "over-issued",
 * the same rule the server applies. `?? 0` everywhere: the Closed pages' lines are thin and carry no MRS figures.
 */
export function mrsLineFigures(line: OverReleaseLine): { ready: number; headroom: number; over: number; postable: number } {
  const ready = Math.max(0, (line.quantityPicked ?? 0) - (line.quantityPosted ?? 0) - (line.postingQuantity ?? 0));
  const requested = line.mrsRequestedQuantity ?? 0;
  if (requested <= 0) return { ready, headroom: ready, over: 0, postable: ready };
  const headroom = Math.max(0, requested - (line.mrsReleasedQuantity ?? 0) - (line.mrsInFlightQuantity ?? 0));
  return { ready, headroom, over: Math.max(0, ready - headroom), postable: Math.min(ready, headroom) };
}

/** The figures `mrsLineFigures` reads — an `items[]` line and a planned line both carry them. */
export type OverReleaseLine = Pick<
  PicklistItemDto,
  'mrsRequestedQuantity' | 'mrsReleasedQuantity' | 'quantityPicked' | 'quantityPosted'
> & {
  /** This picklist's units in open batches on the line. */
  postingQuantity?: number;
  /** What open posts on every picklist hold of the MRS line; planned lines of a by-id read only. */
  mrsInFlightQuantity?: number;
};

/** PT-7 G2 — the item row's over pill: Σ over its planned lines of each line's own over-release. */
export function groupOverRelease(group: PicklistItemGroupDto): number {
  return (group.plannedLines ?? []).reduce((acc, l) => acc + itemOverRelease(l), 0);
}

/** Stacked-bar segment widths, as percentages of the track. */
export interface ProgressSegments {
  postedPct: number;
  inflightPct: number;
  pickedPct: number;
}

export function segments(agg: Pick<PicklistAggregate, 'toPick' | 'picked' | 'posted' | 'inflight'>): ProgressSegments {
  const den = Math.max(agg.toPick, 1);
  return {
    postedPct: (agg.posted / den) * 100,
    inflightPct: (agg.inflight / den) * 100,
    // Picked-awaiting is what is left after the two segments drawn ahead of it — never negative, so a
    // posted figure that briefly exceeds picked (a settling batch) cannot invert the bar.
    pickedPct: (Math.max(agg.picked - agg.posted - agg.inflight, 0) / den) * 100,
  };
}

export interface ProgressFigure {
  key: 'posted' | 'inflight' | 'picked' | 'toPick';
  /**
   * The VISIBLE word after the number — "12 posted". Short, because this renders on every row of the
   * landing table where four long clauses would wrap into a paragraph.
   *
   * 🔑 **Why the middle one is not called "picked".** The figures are DISJOINT, so that bucket holds stock
   * picked and not yet posted. Labelling it "picked" made a part-posted row read "5 posted · 0 picked ·
   * 17 to pick" — and posted stock was picked too, so the honest number under that word is 5, not 0
   * (owner, 2026-09-11). Two ways out: make the figures cumulative, which breaks the correspondence with
   * the bar they sit beneath, or name the bucket accurately. Naming it is the cheaper truth, and
   * `ProgressLegend` already says "Picked · awaiting posting" a few inches below — so the row and its own
   * legend now use one vocabulary instead of two.
   */
  label: string;
  value: number;
}

/**
 * The bar's segments as NUMBERS, in the bar's own order.
 *
 * These are the same four quantities `segments()` turns into widths, which is the point: the row shows a
 * proportion and a magnitude for the same partition, so the two can never tell different stories. They are
 * DISJOINT and sum to `toPick` — `picked` in the aggregate includes what has since been posted or handed
 * to a batch, so the awaiting-posting figure subtracts both rather than double-counting them.
 *
 * **Every state is reported, including zeros** (owner, 2026-09-11), so the figures sit in fixed positions
 * and a column of rows can be read straight down — a row that drops its empty states shifts the remaining
 * dots left and makes two rows in different states look like the same shape.
 *
 * The ONE exception is in-flight, and it is not an inconsistency: `ProgressLegend` draws the in-flight
 * swatch only when something is actually in flight, and a rendered segment with no legend entry is the
 * failure that rule exists to prevent. So it appears on exactly the condition its legend entry does.
 */
export function progressFigures(
  agg: Pick<PicklistAggregate, 'toPick' | 'picked' | 'posted' | 'inflight'>,
): ProgressFigure[] {
  return [
    { key: 'posted' as const, label: 'posted', value: agg.posted },
    { key: 'inflight' as const, label: 'posting', value: agg.inflight },
    { key: 'picked' as const, label: 'awaiting posting', value: Math.max(agg.picked - agg.posted - agg.inflight, 0) },
    { key: 'toPick' as const, label: 'to pick', value: Math.max(agg.toPick - agg.picked, 0) },
  ].filter((f) => f.key !== 'inflight' || f.value > 0);
}

/** Trim a decimal for display: quantities are numerics, so unformatted they read "30.0000". */
export const qty = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));

/** The prototype's `unitWord`: only the generic "units" singularises; a real UoM (KG, PIECE) is written as is. */
export const unitWord = (unit: string, n: number): string =>
  unit === 'units' ? (n === 1 ? 'unit' : 'units') : unit;

export type PillTone = 'success' | 'warning' | 'info' | 'destructive' | 'secondary' | 'muted';

export interface PillState {
  tone: PillTone;
  label: string;
  tooltip: string;
}

/** Picking pill — where the physical work stands. */
export function pickState(picklist: PicklistDto, agg: PicklistAggregate): PillState {
  if (picklist.documentStatus === DocumentStatus.Cancelled) {
    return {
      tone: 'destructive',
      label: 'Cancelled',
      tooltip: 'This picklist was cancelled; picked stock was returned to bins via adjustment.',
    };
  }
  if (agg.picked <= 0) {
    return { tone: 'secondary', label: 'Pending pick', tooltip: 'Nothing picked yet.' };
  }
  if (agg.picked < agg.toPick) {
    return {
      tone: 'warning',
      label: `Picking · ${agg.percent}%`,
      tooltip: `${agg.percent}% of the quantity is physically picked — weighted by quantity, not item count.`,
    };
  }
  return { tone: 'success', label: 'Picked', tooltip: 'All quantities physically picked.' };
}

/**
 * Posting pill — evaluated in order, first match wins.
 *
 * **The two failure rows are load-bearing, not decoration.** D9 ruled that batch mechanics stay off the
 * activity feed, and the Notification Centre use case that would carry them is not built. Until it is, this
 * pill and the detail page's batch strip are the ONLY ambient signals that a post died — flatten them and a
 * dead-lettered MIS is discoverable only by opening the picklist that failed.
 *
 * The two tones must stay distinct. A single destructive "Post failed" covering a batch NetSuite may already
 * have accepted tells a supervisor "failed" and invites a re-post — the exact error the detail page's
 * reconcile flow exists to prevent. Never print "nothing was issued" on the reconcile row.
 *
 * ## `Posted · MIS` means the PICKLIST is posted, not "whatever got picked is posted" (owner, 2026-09-14)
 *
 * The final rung compared `posted` against `picked`, so a picklist that had posted every unit it happened
 * to have picked claimed the success pill while units were still outstanding: PL-202609-00076 showed
 * `Picking · 68%` and `Posted · MIS` side by side with 15 of 22 posted, the two pills contradicting each
 * other on the same row. The denominator is now `toPick`, which is what `itemState` has always used for the
 * per-line pill — the picklist pill was the odd one out, and a line reading `Posted` under a picklist
 * reading `Posted · MIS` was the only consistent thing about it.
 *
 * ⚠️ **A short-closed picklist now reads `Partially posted`, deliberately.** Closing with a reason abandons
 * the unpicked remainder; it does not post it. The document pill says `Closed` and the partial-close reason
 * is on the detail page, so nothing claims the picklist is still owed work — but this pill will no longer
 * say every unit reached NetSuite when seven of them never did. That is the same ruling applied to a
 * terminal document, not an oversight to "fix" by special-casing `DocumentStatus.Closed`.
 */
export function postState(picklist: PicklistDto, agg: PicklistAggregate): PillState {
  const batches: PostingBatchStatusDto[] = picklist.activePostingBatches ?? [];

  if (picklist.documentStatus === DocumentStatus.Cancelled) {
    return { tone: 'muted', label: 'Not posted', tooltip: 'Cancelled picklists are never posted.' };
  }

  const failed = batches.filter((b) => b.status === PostingStatusEnum.PostingFailed);
  if (failed.some((b) => (b.postReach ?? PostReach.NotReached) >= PostReach.Ambiguous)) {
    return {
      tone: 'destructive',
      label: 'Needs reconciling',
      tooltip:
        'A posting batch failed after NetSuite may already have accepted it. Open the picklist and reconcile the batch — do not post again.',
    };
  }
  if (failed.length > 0) {
    return {
      tone: 'destructive',
      label: 'Post failed',
      tooltip: 'A posting batch failed and nothing was issued. Open the picklist to retry it.',
    };
  }

  if (agg.overRelease > 0) {
    return {
      tone: 'destructive',
      label: 'Exceeds MRS qty',
      tooltip:
        `${qty(agg.overRelease)} picked ${agg.unit ?? 'unit'}(s) exceed what the MRS still allows and cannot be posted. ` +
        'Lower the extra in Adjust.',
    };
  }

  if (agg.inflight > 0) {
    return {
      tone: 'info',
      label: `Posting · ${qty(agg.inflight)}`,
      tooltip:
        `${qty(agg.inflight)} picked units are in an open posting batch, on their way to NetSuite. They are ` +
        'locked — not adjustable, and excluded from the next batch — so they cannot be issued twice.',
    };
  }

  if (agg.posted <= 0) {
    return { tone: 'muted', label: 'Not posted', tooltip: 'None of the picked stock has been posted to NetSuite yet.' };
  }
  if (agg.posted < agg.toPick) {
    // Measured against the WHOLE picklist, not against what happens to be picked (owner, 2026-09-14).
    // Both remainders are named, because "partially posted" has two quite different causes and the fix
    // differs: stock picked and not yet posted needs a POST, stock not yet picked needs a PICKER.
    const awaiting = Math.max(agg.picked - agg.posted, 0);
    const unpicked = Math.max(agg.toPick - agg.picked, 0);
    const remainder = [
      awaiting > 0 ? `${qty(awaiting)} picked and awaiting posting` : null,
      unpicked > 0 ? `${qty(unpicked)} still to pick` : null,
    ].filter(Boolean).join(', ');

    return {
      tone: 'warning',
      label: 'Partially posted',
      tooltip: `${qty(agg.posted)} of ${qty(agg.toPick)} posted to NetSuite as MIS — ${remainder}.`,
    };
  }
  return {
    tone: 'success',
    label: 'Posted · MIS',
    tooltip: 'Every unit on this picklist has been posted to NetSuite as MIS.',
  };
}

/** The figures the item-level helpers read. An `items[]` line and an item group both carry them. */
export type ItemFigures = Pick<
  PicklistItemDto,
  'quantityToPick' | 'quantityPicked' | 'quantityPosted' | 'postingQuantity' | 'unitOfMeasure'
>;

/** Item pill — the same ladder, one row at a time (an item group on the detail and landing). */
export function itemState(item: Pick<ItemFigures, 'quantityToPick' | 'quantityPicked' | 'quantityPosted'>): PillState {
  const { quantityToPick: toPick, quantityPicked: picked, quantityPosted: posted } = item;

  if (posted >= toPick && posted > 0) {
    return { tone: 'success', label: 'Posted', tooltip: 'This line is fully posted to NetSuite.' };
  }
  if (posted > 0) {
    return { tone: 'warning', label: `${qty(posted)} posted`, tooltip: `${qty(posted)} of ${qty(toPick)} posted to NetSuite.` };
  }
  if (picked >= toPick && picked > 0) {
    return { tone: 'info', label: 'Picked', tooltip: 'Fully picked, awaiting posting.' };
  }
  if (picked > 0) {
    return { tone: 'warning', label: 'Partial', tooltip: `${qty(picked)} of ${qty(toPick)} picked.` };
  }
  return { tone: 'secondary', label: 'Pending', tooltip: 'Nothing picked from this line yet.' };
}

/** Row meta under the picklist number. */
export const rowMeta = (agg: PicklistAggregate): string =>
  `${agg.itemCount} item${agg.itemCount === 1 ? '' : 's'} · ${agg.fullyPicked} fully picked`;

/**
 * The headline beside the bar, on the landing table. **This function's only consumer is
 * `picklist-table-columns.tsx` — it never reaches the Post dialog.**
 *
 * One unit of measure: the raw total with that unit. MIXED units: one summed total called `units`.
 *
 * ⚠️ **Yes, that adds 50 kg to 3 pcs, and on THIS surface that is the right call** (owner, 2026-09-11).
 * D30 forbade the summed total because "it would appear in the Post dialog immediately before an
 * irreversible NetSuite write" — a real hazard, on a surface where the number is a quantity you ACT on.
 * The landing row is not that surface. Nobody posts from it; the figure is a progress indicator, the
 * column header says `Progress · qty-weighted`, and the exact per-unit split is one hover away in
 * `barTooltip` and spelled out on the detail page. It matches what the activity feed already does with
 * mixed units (dock ruling 2026-08-20).
 *
 * This briefly rendered one total PER UNIT instead — `8 / 8 PAIR · 15 / 15 KILOGRAM · +2 more`. It was
 * accurate and unreadable: four figures and a `+2 more` that advertises information it will not show, in
 * a cell whose job is "how far along is this". Precision belongs where you act, not where you scan.
 */
export const headline = (agg: PicklistAggregate): string =>
  agg.unit
    ? `${qty(agg.picked)} / ${qty(agg.toPick)} ${agg.unit} picked`
    : `${qty(agg.picked)} / ${qty(agg.toPick)} units picked`;

/**
 * `"5 METER · 48 PIECE"` — a per-unit breakdown of one quantity. Never a sum across incompatible units.
 * A line with no unit of measure groups under `"units"`; zero-valued lines are omitted.
 */
export function unitBreakdown(
  items: PicklistItemDto[],
  pick: (i: PicklistItemDto) => number,
): string {
  const sums = new Map<string, number>();
  for (const i of items) {
    const value = pick(i) || 0;
    if (value === 0) continue;
    const key = i.unitOfMeasure || 'units';
    sums.set(key, (sums.get(key) ?? 0) + value);
  }
  return [...sums.entries()].map(([unit, total]) => `${qty(total)} ${unit}`).join(' · ');
}

export function barTooltip(agg: PicklistAggregate): string {
  // Mixed-unit picklists say "units" rather than nothing at all: the figures are a sum across units, and
  // an unlabelled number invites the reader to assume whichever unit they last saw.
  const unit = agg.unit ? ` ${agg.unit}` : ' units';
  const parts = [
    `${qty(agg.posted)}${unit} posted`,
    ...(agg.inflight > 0 ? [`${qty(agg.inflight)}${unit} posting to NetSuite`] : []),
    `${qty(Math.max(agg.picked - agg.posted - agg.inflight, 0))}${unit} picked awaiting posting`,
    `${qty(Math.max(agg.toPick - agg.picked, 0))}${unit} to pick`,
  ];

  // The exact split, for the one surface that can afford it. The headline is deliberately a summary — this
  // is where "8 PAIR and 15 KILOGRAM, not 23 of anything" is available without cluttering every row.
  if (!agg.unit && agg.byUnit.length > 0) {
    parts.push(`per unit — ${agg.byUnit.map((u) => `${qty(u.picked)} / ${qty(u.toPick)} ${u.unit}`).join(', ')}`);
  }

  return parts.join(' · ');
}

export function subLine(agg: PicklistAggregate): string {
  if (agg.posted > 0) return `${qty(agg.posted)} of ${qty(agg.picked)} picked already posted`;
  if (agg.picked > 0) return 'None of the picked stock posted yet';
  return 'Not started';
}

/**
 * Sub-row quantity line — bracketed clauses are omitted when zero. Still carries BOTH numbers, and is
 * still what the progress bar's tooltip shows: on hover you want picked and target together.
 */
export function itemQuantityLine(item: ItemFigures): string {
  const parts: string[] = [];
  if (item.postingQuantity > 0) parts.push(`${qty(item.postingQuantity)} posting`);
  if (item.quantityPosted > 0) parts.push(`${qty(item.quantityPosted)} posted`);
  parts.push(`${qty(item.quantityPicked)} picked / ${qty(item.quantityToPick)} ${item.unitOfMeasure}`);
  return parts.join(' · ');
}

/**
 * What THIS picklist asks for on this line. Its own column since 2026-09-11.
 *
 * It used to exist only as the denominator inside the progress line ("1 picked / 1 PIECE"), which made it
 * the one place a picklist quantity appeared — directly competing with the MRS strip's "Requested" in the
 * row's own expansion. On a picklist taking 5 against an MRS line of 100 the two read "0 picked / 5 PIECE"
 * and "Requested 100" with nothing saying which document either belonged to.
 */
export const itemTargetQty = (item: Pick<ItemFigures, 'quantityToPick' | 'unitOfMeasure'>): string =>
  `${qty(item.quantityToPick)}${item.unitOfMeasure ? ` ${item.unitOfMeasure}` : ''}`;

/**
 * The progress line WITHOUT the target — that has its own column. Picked first, then posted, then posting:
 * the prototype's order (PT-7, O-20 settlement `it.pickedLine`), so the figure the row is about leads.
 */
export function itemPickedLine(item: Pick<ItemFigures, 'quantityPicked' | 'quantityPosted' | 'postingQuantity'>): string {
  const parts = [`${qty(item.quantityPicked)} picked`];
  if (item.quantityPosted > 0) parts.push(`${qty(item.quantityPosted)} posted`);
  if (item.postingQuantity > 0) parts.push(`${qty(item.postingQuantity)} posting`);
  return parts.join(' · ');
}

/** The gap card's adaptive state — what is standing between this picklist and being finished. */
export interface GapCard {
  title: string;
  value: number;
  tone: PillTone;
  subLine: string;
}

/**
 * KPI card 4, evaluated in order. Each tone answers a different question, and the order is what makes the
 * card useful: it always names the FIRST thing standing in the way, rather than the largest number.
 */
export function gapCard(agg: PicklistAggregate): GapCard {
  const toPickLeft = Math.max(agg.toPick - agg.picked, 0);

  if (toPickLeft > 0) {
    return {
      title: 'Still to pick',
      value: toPickLeft,
      tone: 'warning',
      subLine: agg.postable > 0
        ? `+ ${qty(agg.postable)} ready to post`
        : agg.inflight > 0
          ? `${qty(agg.inflight)} in a posting batch`
          : 'Pickers finish on mobile',
    };
  }

  if (agg.overRelease > 0) {
    return {
      title: 'Blocked by MRS qty',
      value: agg.overRelease,
      tone: 'destructive',
      subLine: 'Lower the extra in Adjust',
    };
  }

  if (agg.postable > 0) {
    return {
      title: 'Ready to post',
      value: agg.postable,
      tone: 'info',
      subLine: 'Post to MIS to update NetSuite',
    };
  }

  if (agg.inflight > 0) {
    return {
      title: 'Posting to NetSuite',
      value: agg.inflight,
      tone: 'success',
      subLine: 'Nothing left to press Post for',
    };
  }

  return {
    title: 'Picked, awaiting posting',
    value: Math.max(agg.picked - agg.posted, 0),
    tone: 'success',
    subLine: 'Nothing outstanding',
  };
}

/** What the Post button says, and whether it can be pressed. */
export interface PostButtonState {
  label: string;
  disabled: boolean;
  tooltip: string;
}

/**
 * The Post button's label is a promise about a quantity, so it has to be the quantity the server will
 * actually accept. `agg.postable` already subtracts units in an open batch and drops whole lines over
 * their MRS release, because the claim refuses both with a 400.
 *
 * Evaluated in order. Two of these states did not exist before and both replace a click that 400s:
 * **reach-blocked** (a failed batch NetSuite may already have accepted refuses a new claim outright) and
 * **blocked by MRS qty**. The button used to be enabled in both.
 *
 * The hidden states are not here and must not be moved here: no `issuance.post_picklist` renders NOTHING
 * (PermissionGuard, never a disabled control — a disabled button advertises a capability the role does not
 * have), and a non-picker sees no button either.
 */
export function postButtonState(picklist: PicklistDto, agg: PicklistAggregate): PostButtonState {
  // A frozen document cannot be posted, and the server says so too — the post path throws
  // "Picklist is closed" / refuses a cancelled one. This ladder never read `documentStatus` at all, so a
  // cancelled or closed picklist with postable quantity rendered an ENABLED Post button that could only
  // ever produce a 400. `adjustAvailability` has guarded both states since D7; this is the same rule for
  // the other verb, and it goes FIRST so it cannot be masked by a batch state below.
  if (picklist.documentStatus === DocumentStatus.Cancelled) {
    return {
      label: 'Post to MIS',
      disabled: true,
      tooltip: 'This picklist was cancelled — its records are frozen and nothing can be posted.',
    };
  }
  if (picklist.documentStatus === DocumentStatus.Closed) {
    return {
      label: 'Post to MIS',
      disabled: true,
      tooltip: 'This picklist is closed — its records are frozen. Nothing further can be posted.',
    };
  }

  const batches: PostingBatchStatusDto[] = picklist.activePostingBatches ?? [];
  const reachBlocked = batches.some(
    (b) => b.status === PostingStatusEnum.PostingFailed
      && (b.postReach ?? PostReach.NotReached) >= PostReach.Ambiguous,
  );

  if (reachBlocked) {
    return {
      label: 'Reconcile the failed batch',
      disabled: true,
      tooltip:
        'A posting batch failed after NetSuite may already have accepted it. Reconcile it from the batch '
        + 'card below before posting again — a new post could issue the same stock twice.',
    };
  }

  if (agg.postable > 0) {
    return agg.inflight > 0
      ? {
          label: `Post another batch (${qty(agg.postable)})`,
          disabled: false,
          tooltip: `${qty(agg.inflight)} units are already posting. This posts the ${qty(agg.postable)} that are not.`,
        }
      : {
          label: `Post to MIS (${qty(agg.postable)})`,
          disabled: false,
          tooltip: `Create an MIS in NetSuite for ${qty(agg.postable)} picked units.`,
        };
  }

  if (agg.overRelease > 0) {
    return {
      label: 'Blocked · exceeds MRS qty',
      disabled: true,
      tooltip:
        `${qty(agg.overRelease)} picked units exceed what the MRS still allows, and nothing else is ready, so `
        + 'posting would be refused. Lower the extra in Adjust.',
    };
  }

  // Owner 2026-10-08: the plain Post button, not a spinner — the posting batch card above already shows the batch in
  // flight, and a second loading state on the header read as the page itself still working. Disabled, with the reason.
  if (agg.inflight > 0) {
    return {
      label: 'Post to MIS',
      disabled: true,
      tooltip:
        `All ${qty(agg.inflight)} picked units are in an open posting batch. There is nothing left to post `
        + 'until NetSuite confirms them.',
    };
  }

  return {
    label: 'Post to MIS',
    disabled: true,
    tooltip: 'Nothing is picked and ready to post yet.',
  };
}

/** The neutral document badge beside the title — Draft / Open / Closed / Cancelled. */
export function documentStatusLabel(status: DocumentStatus): string {
  switch (status) {
    case DocumentStatus.Draft: return 'Draft';
    case DocumentStatus.Open: return 'Open';
    case DocumentStatus.Closed: return 'Closed';
    case DocumentStatus.Cancelled: return 'Cancelled';
    default: return 'Unknown';
  }
}

/** Whether Adjust can be pressed on a line, and the sentence that explains it when it cannot. */
export interface AdjustAvailability {
  enabled: boolean;
  reason: string;
}

/**
 * PW-14 §6 — **seven** disabled reasons, not the four the design drew (D7's Closed made six; PT-6 C3's held units
 * make seven).
 *
 * The design's predicate is "picklist Open AND some bin has picked > posted". That leaves the button
 * ENABLED in a state the server refuses outright, which is the one thing a disabled state exists to
 * prevent: while any `posting.mis` job is in flight the whole picklist is frozen, and after a failure that
 * may have reached NetSuite it is frozen again until someone reconciles.
 *
 * ⚠️ **That freeze is picklist-WIDE and must not be narrowed to the committed lines.** After a reached
 * failure the per-bin `PostedQuantity` rolls back to 0, so the posted floor cannot protect itself.
 *
 * Two sentences are the server's own, byte for byte: the post in flight (for Adjust) and the held units. The
 * others are worded for this screen and say the same thing as the server's refusal in other words (F-19,
 * 2026-10-06: this comment used to claim all of them matched).
 */
export function adjustAvailability(
  picklist: PicklistDto,
  // PT-6 — the item group (PicklistItemGroupDto): Adjust works on the whole item. Its display bins, picked and
  // posted sums carry today's names, and its postingQuantity / postingBatchIds are the fold-filled held units.
  item: {
    bins: BinInfoDto[];
    quantityPicked: number;
    quantityPosted: number;
    postingQuantity?: number;
    postingBatchIds?: readonly number[];
    itemCode?: string;
  },
  hasPermission: boolean,
): AdjustAvailability {
  // No permission is a HIDDEN state, not a disabled one — a disabled button advertises a capability the
  // role does not have. The caller drops the control entirely; this value is never rendered.
  if (!hasPermission) return { enabled: false, reason: '' };

  if (picklist.documentStatus === DocumentStatus.Cancelled) {
    return { enabled: false, reason: 'This picklist was cancelled — its records are frozen.' };
  }

  // D7 — Closed freezes Adjust. This is a behaviour CHANGE: adjusting a closed picklist used to be
  // allowed on both layers. The matching server guard ships with it, because a screen that hides a button
  // the API still honours is a rule enforced in exactly one place.
  if (picklist.documentStatus === DocumentStatus.Closed) {
    return { enabled: false, reason: 'This picklist is closed — its records are frozen.' };
  }

  const batches = picklist.activePostingBatches ?? [];
  if (batches.some((b) => b.status === PostingStatusEnum.Posting)) {
    return {
      enabled: false,
      reason: 'This picklist has an MIS post in flight — wait for it to finish before adjusting it.',
    };
  }
  if (batches.some((b) => b.status === PostingStatusEnum.PostingFailed
    && (b.postReach ?? PostReach.NotReached) >= PostReach.Ambiguous)) {
    return {
      enabled: false,
      reason: 'A posting batch failed after NetSuite may already have accepted it. Reconcile that batch before adjusting.',
    };
  }

  // PT-6 C3 (O-41) — an open batch of this picklist (Pending, Claimed or Failed) holds units of this item. A Failed
  // batch's Retry replays its frozen payload, so lowering or moving those units would make every Retry fail again.
  // The server refuses the same way, with the same words.
  if ((item.postingQuantity ?? 0) > 0) {
    const batch = Math.min(...(item.postingBatchIds?.length ? item.postingBatchIds : [0]));
    return {
      enabled: false,
      reason: `Units of ${item.itemCode ?? 'this item'} are in posting batch #${batch}. Retry or cancel that batch before adjusting.`,
    };
  }

  // Per BIN, not on bins.length: an allocated-but-unpicked slot is a row with nothing in it.
  const pickedNow = (item.bins ?? []).reduce((acc, b) => acc + (b.currentlyPickedQuantity || 0), 0);
  if (pickedNow <= 0) {
    return { enabled: false, reason: 'Nothing picked yet — nothing to adjust.' };
  }

  if (item.quantityPicked <= item.quantityPosted) {
    return {
      enabled: false,
      reason: 'Everything picked is already posted to NetSuite — the posted portion is not adjustable from here.',
    };
  }

  return {
    enabled: true,
    reason: 'Reduce the recorded pick on one or more bins. Every adjustment is audited with a reason.',
  };
}

/**
 * ISS-R-023 / ISS-R-025 — whether an item can be removed from a picklist that is open for line edits. The last item
 * never can (cancel the picklist instead). An item with a line that was picked before never can either: removing
 * deletes the line, and its pick record has to stay. The server refuses both.
 */
export function removeAvailability(itemCount: number, pickedBefore: boolean): AdjustAvailability {
  if (itemCount <= 1) return { enabled: false, reason: 'Cannot remove the last item' };
  if (pickedBefore) {
    return { enabled: false, reason: 'This item was picked before, so its record stays. Cancel the picklist to drop it.' };
  }
  return { enabled: true, reason: '' };
}

/**
 * ISS-R-134 / ISS-R-135 — who may act on a picklist, beyond holding the action's permission. The server asks the
 * same two questions (`EnsureAssignedPickerOrManagerAsync`, `EnsureCreatorOrManagerAsync`), so a control the page
 * shows is one the server will accept.
 *
 * Picker-equivalent: an assigned picker, or a holder of manage-pickers. It gates pick, post, close, Adjust and
 * Swap serial. Creator-equivalent: the creator, or a holder of manage-pickers. It gates cancel, line edits and
 * managing pickers. Being able to view all picklists grants neither.
 */
export function picklistRoles(
  picklist: Pick<PicklistDto, 'createdById' | 'pickers'> | null | undefined,
  userId: number | null | undefined,
  canManagePickers: boolean,
): { isCreator: boolean; isPicker: boolean } {
  if (picklist == null) return { isCreator: false, isPicker: false };
  return {
    isCreator: canManagePickers || (userId != null && userId === picklist.createdById),
    isPicker: canManagePickers || (userId != null && picklist.pickers.some((p) => p.userId === userId)),
  };
}

/**
 * PT-6 E4 — whether the bin card's Swap serial can be pressed: the picklist-level reasons of `adjustAvailability`,
 * with the same sentences. No held-units reason: the slot being given another serial is unpicked, so no batch holds
 * it. No permission means no button (the page passes no handler); this value is then never rendered.
 */
export function replaceAvailability(picklist: PicklistDto, hasPermission: boolean): AdjustAvailability {
  if (!hasPermission) return { enabled: false, reason: '' };
  const batches = picklist.activePostingBatches ?? [];

  if (picklist.documentStatus === DocumentStatus.Cancelled) {
    return { enabled: false, reason: 'This picklist was cancelled — its records are frozen.' };
  }
  if (picklist.documentStatus === DocumentStatus.Closed) {
    return { enabled: false, reason: 'This picklist is closed — its records are frozen.' };
  }
  if (batches.some((b) => b.status === PostingStatusEnum.Posting)) {
    return {
      enabled: false,
      reason: 'This picklist has an MIS post in flight — wait for it to finish before adjusting it.',
    };
  }
  if (batches.some((b) => b.status === PostingStatusEnum.PostingFailed
    && (b.postReach ?? PostReach.NotReached) >= PostReach.Ambiguous)) {
    return {
      enabled: false,
      reason: 'A posting batch failed after NetSuite may already have accepted it. Reconcile that batch before adjusting.',
    };
  }
  return { enabled: true, reason: '' };
}

/**
 * One planned line's standing against its MRS line (PT-7 A7): the strip's warning and border.
 *
 * ⚠️ **The field names here are NOT the labels on screen.** The owner reworded the strip on 2026-08-28
 * because "Released 0 / Room left 1" reads as a contradiction — "released" sounds like a permission and
 * means quantity already consumed. The mapping, so a future reader can find the code from the screen:
 *
 * | On screen        | Field       |
 * |------------------|-------------|
 * | Requested        | `requested` |
 * | Already issued   | `released`  |
 * | In flight        | `posting`   |
 * | Can still post   | `roomLeft`  |
 *
 * The field names keep the DTO's vocabulary (`mrsReleasedQuantity`, `postingQuantity`) on purpose, so the
 * wire and the maths stay greppable against the backend.
 */
export interface MrsStandingLine {
  requested: number;
  released: number;
  posting: number;
  /** What may still be posted against this MRS line — shown as "Can still post". */
  roomLeft: number;
  /** Picked, not posted and not already in an open batch — what this line would like to issue next. */
  ready: number;
  /** How far `ready` exceeds `roomLeft`. Zero when it does not. */
  over: number;
  /** False when the MRS demand was never synced — the strip must not print a defaulted figure. */
  known: boolean;
}

/**
 * ⚠️ **Room left subtracts `posting`; the server's refusal does NOT.** They answer different questions and
 * both are correct: `itemOverRelease` mirrors `MisSelectionRules.EnsureWithinMrsRelease` so the *pill* can
 * never contradict the 400, while this figure tells a reader what is genuinely left once the units already
 * in a batch are accounted for. Do not "reconcile" them into one number.
 *
 * `known` is false when `mrsRequestedQuantity` is 0 — the line's MRS demand was never synced. The strip still
 * draws (one per planned line), but its warning and destructive border are withheld: `toPick` is the
 * PICKLIST's demand, not the MRS's, and `quantityPosted` is this picklist's issuance alone, so either
 * substitution paints room that does not exist, and `over = ready` would be a false alarm.
 */
export function plannedLineStanding(line: PicklistPlannedLineDto): MrsStandingLine {
  const requested = line.mrsRequestedQuantity ?? 0;
  const released = line.mrsReleasedQuantity ?? 0;
  const posting = line.postingQuantity ?? 0;
  const roomLeft = Math.max(requested - released - posting, 0);
  // ⚠️ `posting` comes off BOTH sides. Units in an open batch are already spoken for: subtracting them from
  // the room but leaving them in `ready` counted them twice, so a line whose picked units were all on their
  // way to NetSuite read "over by N" for as long as the batch was in flight, while the server (and the
  // item's over pill) said it was fine. Found 2026-09-30 building PT-7's fixtures; `mrsStanding` had it too.
  const ready = Math.max((line.quantityPicked ?? 0) - (line.quantityPosted ?? 0) - posting, 0);

  return {
    requested,
    released,
    posting,
    roomLeft,
    ready,
    over: Math.max(ready - roomLeft, 0),
    known: requested > 0,
  };
}

// ── PT-7 G2 — the item row's per-line facts ────────────────────────────────────────────────────────────────

/**
 * The group's `items[]` rows (its MRS lines, the record), in planned-line order. The Items table builds the
 * id → row map ONCE per render and passes it here, so no row rescans `picklist.items`.
 */
export function groupLines(
  byId: ReadonlyMap<number, PicklistItemDto>,
  group: PicklistItemGroupDto,
): PicklistItemDto[] {
  return (group.plannedLines ?? [])
    .map((l) => byId.get(l.id))
    .filter((row): row is PicklistItemDto => row !== undefined);
}

/**
 * One planned line per MRS number, the first in the order given — what an MRS chip stands for (PT-7 A4, A8, D1).
 *
 * A chip is the MRS, not a line: two lines of one item on one MRS show one chip. Pass one group's
 * `plannedLines` for a row's chips, or every group's in group order (by anchor) for the header's and the
 * landing's. The chip then takes its id, requestor and department from the line returned; one MRS has one
 * number and one requestor, and the department can differ between its lines (O-32), so the card shows the
 * first. This keys a DISPLAY by the number, never an MRS line.
 */
export function firstLinePerMrs(lines: Iterable<PicklistPlannedLineDto>): PicklistPlannedLineDto[] {
  const seen = new Set<string>();
  const out: PicklistPlannedLineDto[] = [];
  for (const line of lines) {
    if (!line.mrsNumber || seen.has(line.mrsNumber)) continue;
    seen.add(line.mrsNumber);
    out.push(line);
  }
  return out;
}

/**
 * The lines' MIS references, each slip once: two lines of one item can go out on one slip. A shared slip's quantity
 * is the lines' total there; it reads null when any of them carries none, because half a total is not a figure.
 */
export function groupMisReferences(lines: readonly PicklistItemDto[]): MisReferenceDto[] {
  const byId = new Map<string, MisReferenceDto>();
  for (const line of lines) {
    for (const ref of line.misReferences ?? []) {
      const seen = byId.get(ref.misId);
      if (!seen) {
        byId.set(ref.misId, { ...ref });
        continue;
      }
      seen.quantity = seen.quantity != null && ref.quantity != null ? seen.quantity + ref.quantity : null;
    }
  }
  return [...byId.values()];
}

/** A slip's name: its transaction number, or "MIS #id" when the post-create read of that number failed. */
export const misName = (ref: Pick<MisReferenceDto, 'misId' | 'misTranId'>): string => ref.misTranId ?? `MIS #${ref.misId}`;

/**
 * PT-7 F8 — what an Add Items call did, counted in ITEM ROWS (groups), the rule PT-1 Scope 12 gives the
 * `PicklistItemsAdded` activity row, so the toast and the row agree.
 *
 * Each request line is found in the RETURNED picklist's groups by its `materialRequestItemId`. A group whose
 * anchor was already on the picklist before the call was increased — a top-up of a line already there, or a
 * new MRS line of an item already there (no new row appears) — and any other group was added. An existing
 * group keeps its anchor, because new lines get higher ids.
 */
export function addItemsCounts(
  anchorsBefore: ReadonlySet<number>,
  after: readonly PicklistItemGroupDto[],
  requestLineIds: readonly number[],
): { added: number; increased: number } {
  const groupOfRequestLine = new Map<number, number>();
  for (const group of after) {
    for (const line of group.plannedLines ?? []) groupOfRequestLine.set(line.materialRequestItemId, group.id);
  }
  const touched = new Set<number>();
  for (const id of requestLineIds) {
    const anchor = groupOfRequestLine.get(id);
    if (anchor !== undefined) touched.add(anchor);
  }
  let increased = 0;
  for (const anchor of touched) if (anchorsBefore.has(anchor)) increased += 1;
  return { added: touched.size - increased, increased };
}

/** One chip on a posting batch: one (item, MRS line) the batch is carrying (PT-7 A11). */
export interface BatchChip {
  /** The posting line — a `PicklistItem.Id`, the planned line's `id`. */
  lineId: number;
  itemCode: string;
  quantity: number;
  unitOfMeasure: string;
  /** Set only when another chip of this batch belongs to the same item: then the MRS number tells them apart. */
  mrsNumber: string | null;
}

/**
 * The batch's chips, one per (item, MRS line). From the batch's FROZEN lines when its contents resolved, each
 * with its own quantity; otherwise from the lines naming the batch in `postingBatchIds`, today's fallback,
 * which is already one per MRS line. A chip names its MRS only when two chips of the batch are one item (one
 * `itemGroups[]` entry): a single-line item needs no MRS to be told apart.
 */
export function batchChips(picklist: PicklistDto, batchId: number): BatchChip[] {
  const byId = new Map((picklist.items ?? []).map((i) => [i.id, i] as const));
  const itemOfLine = new Map<number, number>();
  const mrsOfLine = new Map<number, string>();
  for (const group of picklist.itemGroups ?? []) {
    for (const line of group.plannedLines ?? []) {
      itemOfLine.set(line.id, group.id);
      mrsOfLine.set(line.id, line.mrsNumber);
    }
  }

  const content = (picklist.postingBatchContents ?? []).find((c) => c.postingBatchId === batchId);
  const raw = content?.contentsResolved
    ? content.lines.map((l) => ({
        lineId: l.picklistItemId,
        itemCode: l.itemCode || `#${l.picklistItemId}`,
        quantity: l.quantity ?? 0,
        unitOfMeasure: l.unitOfMeasure,
      }))
    : (picklist.items ?? [])
        .filter((i) => (i.postingBatchIds ?? []).includes(batchId))
        .map((i) => ({ lineId: i.id, itemCode: i.itemCode, quantity: i.postingQuantity ?? 0, unitOfMeasure: i.unitOfMeasure }));

  const perItem = new Map<number, number>();
  for (const chip of raw) {
    const item = itemOfLine.get(chip.lineId) ?? -chip.lineId;
    perItem.set(item, (perItem.get(item) ?? 0) + 1);
  }
  return raw.map((chip) => {
    const shared = (perItem.get(itemOfLine.get(chip.lineId) ?? -chip.lineId) ?? 0) > 1;
    const mrs = mrsOfLine.get(chip.lineId) ?? byId.get(chip.lineId)?.mrsNumber ?? '';
    return { ...chip, mrsNumber: shared && mrs ? mrs : null };
  });
}

/**
 * The group's share of one open batch: the sum of `quantity` over that batch's frozen lines that belong to
 * the group. When the batch's contents are missing or unresolved, the group's whole `postingQuantity` —
 * today's figure. A group's lines are often in different batches (Post refuses a selection spanning
 * requestors), so the group's total would be wrong on each batch's pill.
 */
export function groupBatchQuantity(
  picklist: PicklistDto,
  group: PicklistItemGroupDto,
  batchId: number,
): number {
  const content = (picklist.postingBatchContents ?? []).find((c) => c.postingBatchId === batchId);
  if (!content || !content.contentsResolved) return group.postingQuantity ?? 0;
  const lineIds = new Set((group.plannedLines ?? []).map((l) => l.id));
  return content.lines
    .filter((l) => lineIds.has(l.picklistItemId))
    .reduce((acc, l) => acc + (l.quantity ?? 0), 0);
}
